/**
 * src/components/common/ErrorBoundary.js — Premium error states and skeleton loaders
 */
import React from 'react';
import { Link } from 'react-router-dom';

/* ─── Error Boundary ─────────────────────────────────────────────────────────── */
export class ErrorBoundary extends React.Component {
  constructor(props) { super(props); this.state = { hasError: false, error: null }; }
  static getDerivedStateFromError(error) { return { hasError: true, error }; }
  componentDidCatch(error, info) {
    if (process.env.NODE_ENV === 'development') console.error('[ErrorBoundary]', error, info);
  }
  handleRetry = () => this.setState({ hasError: false, error: null });

  render() {
    if (!this.state.hasError) return this.props.children;
    if (this.props.fallback) return this.props.fallback;
    return (
      <div className="min-h-[50vh] flex items-center justify-center px-4">
        <div className="text-center max-w-md animate-fade-in">
          <div className="w-20 h-20 bg-red-50 rounded-full flex items-center justify-center text-4xl mx-auto mb-5">💥</div>
          <h2 className="text-xl font-black text-gray-800 mb-2"
            style={{fontFamily:'"Playfair Display",serif'}}>Something went wrong</h2>
          <p className="text-sm text-gray-500 mb-6 leading-relaxed">
            An unexpected error occurred.
            {process.env.NODE_ENV === 'development' && this.state.error && (
              <span className="block mt-2 font-mono text-xs text-red-600 bg-red-50 rounded-xl p-2 text-left overflow-auto max-h-28">
                {this.state.error.message}
              </span>
            )}
          </p>
          <div className="flex gap-3 justify-center">
            <button onClick={this.handleRetry}
              className="bg-gradient-to-br from-red-600 to-red-700 text-white font-black
                px-6 py-2.5 rounded-xl shadow-lg hover:-translate-y-0.5 hover:shadow-red-600/30
                transition-all duration-200 active:scale-95 text-sm">
              Try Again
            </button>
            <Link to="/"
              className="border border-gray-200 text-gray-600 hover:bg-gray-50 font-semibold
                px-6 py-2.5 rounded-xl transition-all text-sm">
              Go Home
            </Link>
          </div>
        </div>
      </div>
    );
  }
}

/* ─── Inline error box ───────────────────────────────────────────────────────── */
export function ErrorBox({ error, onRetry, className='' }) {
  if (!error) return null;
  const isNetwork = error?.code === 'NETWORK_ERROR';
  const isTimeout = error?.code === 'REQUEST_TIMEOUT';
  const isServer  = error?.code === 'INTERNAL_SERVER_ERROR';
  const icon    = isNetwork ? '📡' : isTimeout ? '⏱️' : isServer ? '🔧' : '⚠️';
  const message = isNetwork
    ? 'No internet connection. Check your network and try again.'
    : isTimeout
    ? 'The request timed out. The server may be slow — please retry.'
    : error?.message || 'Failed to load data.';

  return (
    <div className={`rounded-2xl border border-red-200 bg-red-50/80 px-5 py-4
      flex items-start gap-3 animate-fade-in ${className}`}>
      <span className="text-2xl flex-shrink-0 mt-0.5">{icon}</span>
      <div className="flex-1 min-w-0">
        <p className="text-sm font-black text-red-700 mb-1">Failed to load</p>
        <p className="text-xs text-red-600/80 leading-relaxed">{message}</p>
      </div>
      {onRetry && (
        <button onClick={onRetry}
          className="flex-shrink-0 text-xs font-black text-red-600 border border-red-300
            hover:bg-red-100 px-3 py-1.5 rounded-lg transition-all hover:-translate-y-0.5 active:scale-95">
          Retry ↺
        </button>
      )}
    </div>
  );
}

/* ─── Network banner ─────────────────────────────────────────────────────────── */
export function NetworkBanner() {
  const [, setOffline]  = React.useState(!window.navigator.onLine); // offline state — only setter used
  const [visible,  setVisible]  = React.useState(!window.navigator.onLine);
  const [restored, setRestored] = React.useState(false);

  React.useEffect(() => {
    const goOffline = () => { setOffline(true);  setVisible(true); setRestored(false); };
    const goOnline  = () => {
      setOffline(false); setRestored(true);
      setTimeout(() => { setVisible(false); setRestored(false); }, 3000);
    };
    window.addEventListener('offline', goOffline);
    window.addEventListener('online',  goOnline);
    return () => { window.removeEventListener('offline', goOffline); window.removeEventListener('online', goOnline); };
  }, []);

  if (!visible) return null;
  return (
    <div className={`fixed top-0 inset-x-0 z-[9998] flex items-center justify-center
      gap-3 py-2.5 px-4 text-sm font-bold text-white transition-colors
      ${restored ? 'bg-green-600' : 'bg-red-700'}`}>
      {restored ? '✓ Connection restored' : '📡 No internet connection — some features may not work'}
    </div>
  );
}

/* ─── Skeleton loaders ───────────────────────────────────────────────────────── */
// Warm shimmer matching the cream background palette
export function Skeleton({ className='' }) {
  return (
    <div className={`rounded-xl overflow-hidden ${className}`}
      style={{
        background:'linear-gradient(90deg, #f0e8d8 25%, #fdf5e8 50%, #f0e8d8 75%)',
        backgroundSize:'400px 100%',
        animation:'skeleton-wave 1.6s ease infinite',
      }}/>
  );
}

export function ProductCardSkeleton() {
  return (
    <div className="bg-white rounded-2xl overflow-hidden border border-gray-100/80
      shadow-[0_2px_12px_rgba(0,0,0,0.05)]">
      <Skeleton className="h-48 rounded-none"/>
      <div className="p-4 space-y-3">
        <Skeleton className="h-3 w-1/3"/>
        <Skeleton className="h-4 w-4/5"/>
        <Skeleton className="h-4 w-1/2"/>
        <Skeleton className="h-10 w-full mt-2"/>
      </div>
    </div>
  );
}

export function TableRowSkeleton({ cols=5 }) {
  return (
    <tr>
      {[...Array(cols)].map((_,i) => (
        <td key={i} className="px-5 py-4">
          <Skeleton className={`h-4 ${i===0?'w-24':i===cols-1?'w-16':'w-full'}`}/>
        </td>
      ))}
    </tr>
  );
}
